import { cloneDeep } from 'lodash';
import React from 'react';
import { assign } from 'xstate';
import { Box, Typography } from '@mui/material';
import { IntlShape } from 'react-intl';

import { theme } from '../../theme/theme';
import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';

export interface CoacheeMessageComponent extends BaseComponent {
  type: 'coacheeMessage';
  props: CoacheeMessageProps;
}
export interface CoacheeMessageProps {
  message: string;
}
export const CoacheeMessage: React.FC<CoacheeMessageProps> = ({ message }) => {
  return (
    <Box
      sx={{
        display: 'block',
        padding: '15px',
        borderRadius: '6px 6px 0 6px',
        backgroundColor: '#fff2dd' /*coachee-message-background-color*/,
        fontFamily: theme.typography.fontFamily,
      }}
    >
      <Typography>{message}</Typography>
    </Box>
  );
};

/*
  ! renderCoacheeMessageAction - Singular => s. Plural below!
  used for generating a single messages, when the input is a string
*/
const checkMessageObject = (
  messageOrObject: any,
  objectPropertyContainingMessage: any,
  intl: any
) => {
  let message: any = '';
  if (
    typeof messageOrObject === 'string' ||
    typeof messageOrObject === 'number'
  ) {
    message = messageOrObject as string;
  } else {
    message = (messageOrObject as Record<string, any>)[
      objectPropertyContainingMessage
    ];
    const messagetype = message
      ? (messageOrObject as Record<string, any>)['type']
      : undefined;
    if (messagetype === 'date') {
      message = new Intl.DateTimeFormat(intl.locale, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      }).format(Date.parse(message + ''));
    } else if (messagetype === 'time') {
      message = new Intl.DateTimeFormat(intl.locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(Date.parse(message + ''));
    } else if (message instanceof Date) {
      message = '';
    }
  }
  return message === undefined || message === null ? '' : message;
};

//
//  renderAction for state machine widget stack
//
export const renderCoacheeMessageAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const getValueFrom = actionMetadata.action.payload.getValueFrom;
    const objectPropertyContainingMessage =
      actionMetadata.action.payload.objectPropertyContainingMessage;

    let messageOrObject = context.userData[getValueFrom];
    let message: any = '';

    // note: we have to check for !== undefined. It's not sufficient to check for
    // "if(messageOrObject)" because this is false if messageOrObject is of type
    // number and is 0 (which is the case for the rating input!)
    if (messageOrObject !== undefined && messageOrObject !== null) {
      message = checkMessageObject(
        messageOrObject,
        objectPropertyContainingMessage,
        intl
      );
      newContext.widgetData.push({
        type: 'coacheeMessage',
        temporary: actionMetadata.action.temporary ?? false,
        props: {
          message: message,
        },
      });
    } else {
      newContext.widgetData.push({
        type: 'coacheeMessage',
        temporary: actionMetadata.action.temporary ?? false,
        props: { message: '' },
      });
    }

    return newContext;
  });
};

/*
  ! renderCoacheeMessagesAction - Plural
  used for generating several messages, when the input is an array of string
*/
export const renderCoacheeMessagesAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    const objectPropertyContainingMessage =
      payload.objectPropertyContainingMessage;

    const messages = context.userData[getValueFrom];

    if (messages) {
      messages.forEach(
        (
          messageOrObject: number | string | Record<string, any> | undefined
        ) => {
          let message = '';
          if (messageOrObject !== undefined && messageOrObject !== null) {
            message = checkMessageObject(
              messageOrObject,
              objectPropertyContainingMessage,
              intl
            );

            newContext.widgetData.push({
              type: 'coacheeMessage',
              temporary: actionMetadata.action.temporary ?? false,
              props: { message: message },
            });
          } else {
            newContext.widgetData.push({
              type: 'coacheeMessage',
              temporary: actionMetadata.action.temporary ?? false,
              props: { message: '' },
            });
          }
        }
      );
    }
    return newContext;
  });
};
